import { useIntl } from '@faceup/localization'
import { type RouterOutputs, trpc, useMutation, useQuery, useQueryClient } from '@faceup/trpc'
import { Card } from '@faceup/ui'
import { Alert, Button, Popconfirm, Table, Tag, useMessage } from '@faceup/ui-base'
import { useEffect, useRef } from 'react'

type ForcedMigrationRun = NonNullable<
  RouterOutputs['legacyInvestigationMigration']['forced']['lastRun']
>

const runningStatuses: ForcedMigrationRun['status'][] = ['pending', 'active']

const outcomeLabels = {
  Moved: 'Moved',
  SkippedAlreadyRunning: 'Skipped, another move was running',
  StoppedLockLost: 'Stopped, a member took over the move',
} as const

/**
 * The forced migration of legacy investigations (ENG-2656). Runs in the region you are connected
 * to, as a background job, and can be run again: it only picks up what is left.
 */
export const ForcedLegacyInvestigationMigrationCard = () => {
  const message = useMessage()
  const queryClient = useQueryClient()
  const { data: overview, isLoading: isOverviewLoading } = useQuery(
    trpc.legacyInvestigationMigration.forced.overview.queryOptions()
  )
  const { data: lastRun } = useQuery({
    ...trpc.legacyInvestigationMigration.forced.lastRun.queryOptions(),
    refetchInterval: query => {
      const status = query.state.data?.status
      return status && runningStatuses.includes(status) ? 5_000 : false
    },
  })
  const isRunning = lastRun ? runningStatuses.includes(lastRun.status) : false

  // Starting only queues the run, so the overview is read again once the worker has finished it.
  // Only a run seen finishing counts: one that had finished before the card opened changes nothing.
  const wasRunning = useRef(isRunning)
  useEffect(() => {
    if (wasRunning.current && !isRunning) {
      void queryClient.invalidateQueries({
        queryKey: trpc.legacyInvestigationMigration.forced.overview.queryKey(),
      })
    }
    wasRunning.current = isRunning
  }, [isRunning, queryClient])

  const { mutate: start, isPending: isStarting } = useMutation(
    trpc.legacyInvestigationMigration.forced.start.mutationOptions({
      onSuccess: async () => {
        message.success('The move is queued')
        await queryClient.invalidateQueries({
          queryKey: trpc.legacyInvestigationMigration.forced.pathKey(),
        })
      },
    })
  )

  const totals = (overview ?? []).reduce(
    (sum, institution) => ({
      remaining: sum.remaining + institution.remainingCount,
      migratable: sum.migratable + institution.migratableCount,
      withoutInvestigator: sum.withoutInvestigator + institution.withoutInvestigatorCount,
      e2ee: sum.e2ee + (institution.isE2EE ? institution.remainingCount : 0),
    }),
    { remaining: 0, migratable: 0, withoutInvestigator: 0, e2ee: 0 }
  )

  return (
    <Card>
      <Card.Header>Force-migrate legacy investigations</Card.Header>
      <Card.Content>
        <p className='mb-4 text-colorTextSecondary'>
          Moves every legacy investigation left in the region you are connected to into the
          Investigations module, with nobody as the actor. In E2EE accounts the server cannot read
          the case, so their investigations move without the original report (case snapshot) and its
          attachments. A case nobody can be assigned to (nobody with access holds Investigations,
          nor does the Account owner) stays legacy for Customer Success to sort out. Safe to run
          again.
        </p>
        <p className='mb-4'>
          {totals.remaining} left: {totals.migratable} will move, {totals.withoutInvestigator}{' '}
          without an investigator. {totals.e2ee} of them in E2EE accounts, which move without the
          original report.
        </p>
        <Popconfirm
          title='Move every legacy investigation left in this region?'
          onConfirm={() => start({})}
        >
          <Button type='primary' loading={isStarting || isRunning} className='mb-4'>
            {isRunning ? 'Moving…' : 'Move all in this region'}
          </Button>
        </Popconfirm>
        <Table
          loading={isOverviewLoading}
          rowKey='id'
          dataSource={overview ?? []}
          pagination={{ pageSize: 20 }}
          scroll={{ x: 'max-content' }}
          columns={[
            { title: 'Institution', dataIndex: 'name' },
            {
              title: 'E2EE',
              render: institution => (institution.isE2EE ? <Tag color='orange'>E2EE</Tag> : null),
            },
            { title: 'Left', dataIndex: 'remainingCount' },
            { title: 'Will move', dataIndex: 'migratableCount' },
            { title: 'No investigator', dataIndex: 'withoutInvestigatorCount' },
            {
              title: 'Actions',
              render: institution => (
                <div className='flex justify-end'>
                  <Popconfirm
                    title={`Move the legacy investigations of ${institution.name}?`}
                    onConfirm={() => start({ institutionId: institution.id })}
                    placement='topRight'
                  >
                    <Button ghost type='primary' disabled={isRunning}>
                      Move
                    </Button>
                  </Popconfirm>
                </div>
              ),
            },
          ]}
        />
        {lastRun && <LastForcedMigrationRun run={lastRun} />}
      </Card.Content>
    </Card>
  )
}

type LastForcedMigrationRunProps = {
  run: ForcedMigrationRun
}

const LastForcedMigrationRun = ({ run }: LastForcedMigrationRunProps) => {
  const { formatDate } = useIntl()

  return (
    <div className='mt-6' aria-live='polite'>
      <h3 className='mb-2 font-semibold'>
        Last run: {run.status}, started{' '}
        {formatDate(run.createdAt, { dateStyle: 'medium', timeStyle: 'short' })}
      </h3>
      {run.error && <Alert className='mb-4' type='error' message={run.error} />}
      {run.institutions && (
        <Table
          rowKey='institutionId'
          dataSource={run.institutions}
          pagination={{ pageSize: 20 }}
          scroll={{ x: 'max-content' }}
          expandable={{
            rowExpandable: institution =>
              institution.failures.length > 0 || institution.withoutInvestigatorCaseIds.length > 0,
            expandedRowRender: institution => (
              <ul className='list-disc ps-6'>
                {institution.failures.map(failure => (
                  <li key={failure.caseId}>
                    Case {failure.caseId}: {failure.error} ({failure.message})
                  </li>
                ))}
                {institution.withoutInvestigatorCaseIds.map(caseId => (
                  <li key={caseId}>Case {caseId}: nobody can be assigned</li>
                ))}
              </ul>
            ),
          }}
          columns={[
            { title: 'Institution', dataIndex: 'name' },
            {
              title: 'Outcome',
              render: institution =>
                institution.isWithoutSnapshot && institution.outcome === 'Moved'
                  ? 'Moved without the original report'
                  : outcomeLabels[institution.outcome],
            },
            { title: 'Moved', dataIndex: 'movedCount' },
            {
              title: 'No investigator',
              render: institution => institution.withoutInvestigatorCaseIds.length,
            },
            { title: 'Failed', render: institution => institution.failures.length },
          ]}
        />
      )}
    </div>
  )
}
